import React, { createContext, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import SftGlobalData from '../../library/SftGlobalData';
import UnlayerDocumentImport from '../../components/UnlayerDocumentImport';

/**
 * Webshop flavour of <UnlayerDocumentImport>: images from the imported document
 * go into the shop's own library through the existing base64 upload
 * (mini-api POST /admin/upload via the auth proxy), exactly like every other
 * admin upload.
 *
 * The Unlayer editors sit at very different depths (page tabs, modals, cards)
 * and most of them never receive the api key / webshop as props, so those come
 * from a context that WebshopConfigScreen provides once. An editor only adds:
 *
 *   <ShopDocumentImport getEditor={() => editorRef.current?.editor} t={t} />
 */
const ShopImportContext = createContext(null);

export const ShopDocumentImportProvider = ({ api, login, webshopId, children }) => {
    const value = useMemo(
        () => ({ api, webshopId, sftUserId: login?.sft_user_id, user: login?.login }),
        [api, webshopId, login?.sft_user_id, login?.login],
    );
    return <ShopImportContext.Provider value={value}>{children}</ShopImportContext.Provider>;
};

ShopDocumentImportProvider.propTypes = {
    api:       PropTypes.string,
    login:     PropTypes.object,
    webshopId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    children:  PropTypes.node,
};

const ShopDocumentImport = (props) => {
    const ctx = useContext(ShopImportContext);

    const uploadImage = useMemo(() => {
        if (!ctx?.api || !ctx?.webshopId) return undefined;
        return async (dataUrl, filename) => {
            const res = await SftGlobalData.post('webshop&action=proxy', {
                key:        ctx.api,
                webshop_id: ctx.webshopId,
                sftUserId:  ctx.sftUserId,
                user:       ctx.user,
                method:     'POST',
                path:       '/admin/upload?prependHost=true',
                body:       { file_base64: dataUrl, filename },
            });
            return (res?.success && res.data?.url) ? res.data.url : null;
        };
    }, [ctx?.api, ctx?.webshopId, ctx?.sftUserId, ctx?.user]);

    // Rendered outside the provider (isolated preview, storybook-style use): nothing to convert with.
    if (!ctx?.api) return null;

    return <UnlayerDocumentImport apiKey={ctx.api} uploadImage={uploadImage} {...props} />;
};

export default ShopDocumentImport;
